import { useTranslation } from 'react-i18next';
import { useFileConvert } from '../../hooks/useFileConvert';
import { useState, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import ConversionSuccess from '../../components/feature/ConversionSuccess';

export default function PdfToPptPage() {
  const { t } = useTranslation();
  const {
    file, status, progress, error, downloadUrl, fileName,
    setFile, startConversion, download, reset
  } = useFileConvert();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    // Set page title
    document.title = 'PDF to PowerPoint Converter - Free Online | FreePDFTools';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Convert PDF files to PowerPoint presentations online for free. Transform documents into editable slides. Fast, secure, and easy to use.');
    
    // Set canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/pdf-to-ppt`);
    
    // SoftwareApplication Schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PDF to PowerPoint Converter",
      "url": `${siteUrl}/pdf-to-ppt`,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Convert PDF files to PowerPoint presentations online for free. Transform documents into editable slides.",
      "featureList": [
        "Convert PDF pages to PowerPoint slides",
        "Preserve images and graphics quality",
        "Maintain layout and formatting",
        "Editable presentation output",
        "No registration required"
      ],
      "screenshot": `${siteUrl}/og-image.png`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.6",
        "ratingCount": "10320",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Convert PDF to PowerPoint",
      "description": "Step-by-step guide to convert PDF files to PowerPoint presentations online",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Upload PDF File",
          "text": "Click 'Select File' or drag and drop your PDF file into the upload area",
          "url": `${siteUrl}/pdf-to-ppt#step1`
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Convert to PowerPoint",
          "text": "The system will automatically convert each PDF page into an editable PowerPoint slide",
          "url": `${siteUrl}/pdf-to-ppt#step2`
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Download PPT File",
          "text": "Once conversion is complete, download your PowerPoint presentation file",
          "url": `${siteUrl}/pdf-to-ppt#step3`
        }
      ],
      "totalTime": "PT2M"
    };

    const softwareScript = document.createElement('script');
    softwareScript.type = 'application/ld+json';
    softwareScript.text = JSON.stringify(softwareSchema);
    document.head.appendChild(softwareScript);

    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.text = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    return () => {
      document.head.removeChild(softwareScript);
      document.head.removeChild(howToScript);
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      startConversion('pdf-to-ppt', ['pdf']);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      startConversion('pdf-to-ppt', ['pdf']);
    }
  };

  const isProcessing = status === 'uploading' || status === 'converting' || status === 'downloading';

  return (
    <Layout>
      <div className="bg-white min-h-screen">
        <div className="w-full bg-gray-50 border-b border-dashed border-gray-300 py-3">
          <div className="max-w-4xl mx-auto text-center text-gray-400 text-sm">{t('ads.topBanner')} 728×90</div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-slideshow-line text-3xl text-orange-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pdfToPpt.title')}</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('pdfToPpt.description')}</p>
          </div>

          <div className="mb-10">
            {status === 'idle' && (
              <div
                className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${
                  isDragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50/40'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                  <i className="ri-upload-cloud-2-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.dragDrop')}</h3>
                <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                  <span className="px-8 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors inline-block whitespace-nowrap">
                    {t('upload.selectFile')}
                  </span>
                </label>
                <p className="text-sm text-gray-400 mt-5">{t('upload.fileSize')}</p>
              </div>
            )}

            {isProcessing && (
              <div className="border-2 border-orange-200 rounded-2xl p-12 text-center bg-orange-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-loader-4-line text-4xl text-orange-600 animate-spin"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {status === 'uploading' && t('upload.uploading')}
                  {status === 'converting' && t('upload.converting')}
                  {status === 'downloading' && t('upload.preparing')}
                </h3>
                <p className="text-gray-600 mb-4">{file?.name}</p>
                <div className="max-w-md mx-auto">
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className="bg-orange-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="text-sm text-gray-500">{progress}%</p>
                </div>
              </div>
            )}

            {status === 'completed' && downloadUrl && (
              <ConversionSuccess
                fileName={fileName}
                originalName={file?.name}
                accentColor="orange"
                onDownload={download}
                onReset={reset}
              />
            )}

            {status === 'error' && error && (
              <div className="border-2 border-red-200 rounded-2xl p-12 text-center bg-red-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-error-warning-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.error')}</h3>
                <p className="text-red-600 mb-6">{error.message}</p>
                <button onClick={reset} className="px-8 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap">{t('upload.retry')}</button>
              </div>
            )}
          </div>

          <div className="w-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl py-8 mb-10 text-center text-gray-400 text-sm">
            {t('ads.middleBanner')} 728×90
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-layout-grid-line', key: 'feature1' },
              { icon: 'ri-image-line', key: 'feature2' },
              { icon: 'ri-presentation-line', key: 'feature3' },
            ].map((f) => (
              <div key={f.key} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-orange-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900">{t(`pdfToPpt.${f.key}`)}</h3>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t('upload.howItWorks')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="text-center">
                  <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">{n}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t(`upload.step${n}`)}</h3>
                  <p className="text-sm text-gray-600">{t(`upload.step${n}Desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
